import React, { useState, useEffect } from "react";
import { X, Loader2, Lock, User, Mail, Key, Github, Gitlab } from "lucide-react";
import { buildApiUrl } from "../config/api";

interface AuthModalProps {
  onClose: () => void;
  onLogin: (token: string, user: any) => void;
}

type AuthMode = "login" | "signup" | "verify";

export function AuthModal({ onClose, onLogin }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Handle OAuth callback (token from query params after backend redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const userStr = urlParams.get("user");
    const error = urlParams.get("error");

    if (error) {
      setError(`OAuth failed: ${error}`);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (token && userStr) {
      try {
        const user = JSON.parse(decodeURIComponent(userStr));
        onLogin(token, user);
        // Clean up URL
        window.history.replaceState({}, "", window.location.pathname);
      } catch (e) {
        setError("Failed to parse user data");
      }
    }
  }, [onLogin]);

  const handleOAuthLogin = async (provider: "github" | "gitlab") => {
    setLoading(true);
    try {
      // Backend will handle the OAuth callback directly
      const res = await fetch(buildApiUrl(`/api/auth/${provider}`));
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `${provider} OAuth not configured`);
      }

      // Redirect to OAuth provider
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === "login") {
        const res = await fetch(buildApiUrl("/api/auth/login"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });

        const data = await res.json();

        if (!res.ok) {
          if (data.requiresVerification) {
            setUserId(data.userId);
            setMode("verify");
            setMessage("Please verify your email to continue");
            return;
          }
          throw new Error(data.error || "Authentication failed");
        }

        onLogin(data.token, data.user);
      } else if (mode === "signup") {
        const res = await fetch(buildApiUrl("/api/auth/signup"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, email, password }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Signup failed");
        }

        setUserId(data.userId);
        setMode("verify");
        setMessage(data.emailSent 
          ? "Verification code sent to your email!" 
          : "Account created. Please verify your email."
        );
      } else if (mode === "verify") {
        const res = await fetch(buildApiUrl("/api/auth/verify-email"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, code: verificationCode }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Verification failed");
        }

        onLogin(data.token, data.user);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl("/api/auth/resend-code"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to resend code");
      }

      setMessage(data.emailSent ? "New verification code sent!" : "Code resent");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "login": return "Welcome Back";
      case "signup": return "Create Account";
      case "verify": return "Verify Email";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200/60 bg-zinc-50/50">
          <h2 className="text-lg font-semibold text-zinc-900">
            {getTitle()}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* OAuth Buttons */}
          {mode !== "verify" && (
            <>
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleOAuthLogin("github")}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-zinc-900 text-white rounded-xl font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
                >
                  <Github className="w-5 h-5" />
                  Continue with GitHub
                </button>
                <button
                  onClick={() => handleOAuthLogin("gitlab")}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-[#FC6D26] text-white rounded-xl font-medium hover:bg-[#e55a1b] transition-colors disabled:opacity-50"
                >
                  <Gitlab className="w-5 h-5" />
                  Continue with GitLab
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-zinc-500">Or continue with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "verify" ? (
              <div>
                <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                  Verification Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-4 w-4 text-zinc-400" />
                  </div>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full pl-10 pr-3 py-2 bg-zinc-50 border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all text-center tracking-[0.5em] font-mono text-lg"
                    placeholder="000000"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Enter the 6-digit code sent to your email
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                    Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-zinc-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-zinc-50 border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all"
                      placeholder="Enter your username"
                    />
                  </div>
                </div>

                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-zinc-400" />
                      </div>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 bg-zinc-50 border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-zinc-900 mb-2 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-zinc-400" />
                    </div>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-zinc-50 border border-zinc-200/60 rounded-xl text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-300 transition-all"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100">
                {error}
              </div>
            )}

            {message && (
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm border border-emerald-100">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign In" : mode === "signup" ? "Sign Up" : "Verify Email"}
            </button>
          </form>

          {mode === "verify" ? (
            <div className="mt-6 text-center text-sm">
              <button
                onClick={resendCode}
                disabled={loading}
                className="text-zinc-600 hover:text-zinc-900 font-medium"
              >
                Didn't receive code? Resend
              </button>
              <div className="mt-2">
                <button
                  onClick={() => {
                    setMode("login");
                    setError(null);
                    setMessage(null);
                    setVerificationCode("");
                  }}
                  className="text-zinc-400 hover:text-zinc-600"
                >
                  Back to login
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-6 text-center text-sm text-zinc-500">
              {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => {
                  setMode(mode === "login" ? "signup" : "login");
                  setError(null);
                  setMessage(null);
                }}
                className="text-zinc-900 font-semibold hover:underline"
              >
                {mode === "login" ? "Sign up" : "Sign in"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
